import { inject } from "mobx-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { compose } from "nvent-web/utils/compose";
import PageTitle from "nvent-web/App/components/PageTitle";
import { PrimaryBlueButton } from "nvent-web/components/Button";
import { LoadingSection } from "nvent-web/components/LoadingSection/LoadingSection";
import { LoadingSpinner } from "nvent-web/components/LoadingSpinner/LoadingSpinner";
import style from "./ProjectCommissionReport.module.scss";
import { usePolling } from "nvent-web/hooks/usePolling";
import PublicApi from "nvent-web/services/PublicApi";
import { CommissionReportsGenerationStatus } from "nvent-web/types/CommissionReportsGenerationStatus";
import { type ProjectCommissionReportsGeneration } from "nvent-web/types/ProjectsCommissionReportsGeneration";
import { STOP_POLLING_STATUSES } from "nvent-web/utils/polling";
import { withRouter, type WithRouterProps } from "~/nvent-web/utils/withRouter";

interface ProjectCommissionReportProps extends WithRouterProps {
  projectId: string;
  publicApi: PublicApi;
}

type ReportGenerationState = {
  url: ProjectCommissionReportsGeneration["url"];
  status: CommissionReportsGenerationStatus;
  projectName: ProjectCommissionReportsGeneration["project"]["name"];
};

const ProjectCommissionReport = (props: ProjectCommissionReportProps) => {
  const [isPageLoading, setIsPageLoading] = useState<boolean>(true);
  const [isTokenInvalid, setIsTokenInvalid] = useState<boolean>(false);
  const [reportGeneration, setReportGeneration] =
    useState<ReportGenerationState | null>(null);
  const projectId = props.params.projectId;
  const projectToken =
    getProjectTokenFromQueryString(window.location.search) || "";

  if (!projectId) {
    throw new Error("Project ID is missing");
  }

  usePolling(
    async () => {
      try {
        const { status, url, project } = await fetchGenerationStatus({
          api: props.publicApi,
          projectId,
          projectToken,
        });
        setReportGeneration({ status, url, projectName: project.name });
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (_error) {
        setIsTokenInvalid(true);
      } finally {
        setIsPageLoading(false);
      }
    },
    {
      isActive:
        !reportGeneration ||
        !STOP_POLLING_STATUSES.includes(reportGeneration.status),
    }
  );

  const handleDownloadButtonClick = async () => {
    if (!reportGeneration || !reportGeneration.url?.length)
      throw new Error("Attempt to download report without an url");

    await props.publicApi.commissionReport.download(projectId, projectToken);
  };

  if (isTokenInvalid)
    return (
      <div className={style.container}>
        <p className={style.tokenInvalidTitle}>
          <FormattedMessage id="commissionReportDownload.invalidToken" />
        </p>
        <p className={style.tokenInvalidDescription}>
          <FormattedMessage id="commissionReportDownload.urlYouProvidedIsInvalid" />
        </p>
      </div>
    );

  return (
    <div className={style.container}>
      <PageTitle>
        <FormattedMessage id="commissionReportDownload.commissionReportDownload" />
      </PageTitle>
      <div className={style.content}>
        {isPageLoading && <LoadingSection />}
        {!isPageLoading && reportGeneration && (
          <>
            <p className={style.projectName}>{reportGeneration.projectName}</p>
            <StatusIndicator status={reportGeneration.status} />
            {reportGeneration.url?.length && (
              <DownloadButton onClick={handleDownloadButtonClick} />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const getProjectTokenFromQueryString = (queryString: string) => {
  const queryParams = new URLSearchParams(queryString);

  return queryParams.get("projectToken");
};

const fetchGenerationStatus = async ({
  api,
  projectId,
  projectToken,
}: {
  api: PublicApi;
  projectId: string;
  projectToken: string;
}) => {
  const { data: existingGenerationData } =
    await api.commissionReportGeneration.get(projectId, projectToken);

  if (
    existingGenerationData.status !==
    CommissionReportsGenerationStatus.notRequested
  )
    return existingGenerationData;

  await api.commissionReportGeneration.create(projectId, projectToken || "");
  const { data: createdGenerationData } =
    await api.commissionReportGeneration.get(projectId, projectToken);

  return createdGenerationData;
};

const StatusIndicator = ({
  status,
}: {
  status: CommissionReportsGenerationStatus;
}) => {
  if (status === CommissionReportsGenerationStatus.notRequested) return <></>;
  if (status === CommissionReportsGenerationStatus.noRooms) return <></>;

  const statusMap = {
    requested: "commissionReportDownload.reportInGenerationQueue",
    generating: "commissionReportDownload.reportBeingGenerated",
    frozen: "commissionReportDownload.reportGenerationSuspended",
    generated: "commissionReportDownload.reportGenerated",
  };
  const messageId = statusMap[status];

  return (
    <div className={style.statusIndicator}>
      <FormattedMessage id={messageId} />
      {["requested", "generating"].includes(status) && (
        <LoadingSpinner className={style.spinner} />
      )}
    </div>
  );
};

const DownloadButton = ({ onClick }: { onClick: () => void }) => {
  return (
    <PrimaryBlueButton onClick={onClick} className={style.downloadButton}>
      <FormattedMessage id="commissionReportDownload.downloadReport" />
    </PrimaryBlueButton>
  );
};

const ProjectCommissionReportComponent = compose(
  withRouter,
  inject("publicApi")
)(ProjectCommissionReport);

export default ProjectCommissionReportComponent;
